import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { IHeaderParams } from '@ag-grid-community/core';
import { Component, computed, signal, ViewChildren, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { AuxIconName } from '@shared/components/icon/icon';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

export interface AgInlineHeaderComponentParams extends IHeaderParams {
  autoFocus?: boolean;
  editable: boolean;
  field: string;
  getHeaderNames: () => Record<string, string>;
  headerErrorState: WritableSignal<Record<string, boolean>>;
  icon?: AuxIconName;
  onBlur(name: string): void;
  onRemoveColumn: () => Promise<void>;
  removable: boolean;
  removeColumnDisabled: boolean;
  removeColumnTooltip: string;
}

@Component({
  imports: [InputComponent, FormsModule, IconComponent, TooltipDirective, ClickOutsideDirective],
  styles: [
    `
      :host {
        @apply h-full w-full;
      }

      ::ng-deep .inline-header-input {
        height: 30.5px;
      }
    `,
  ],
  template: `
    <div class="flex h-full w-full items-center justify-between">
      @if (edit()) {
        <aux-input
          #inputComponent
          inputClassName="inline-header-input"
          class="w-full"
          (auxClickOutside)="onBlur()"
          (keydown.escape)="onBlur()"
          (keydown.enter)="onBlur()"
          (keydown.tab)="onBlur()"
          (keydown.shift.tab)="onBlur()"
          [ngModel]="headerName()"
          (ngModelChange)="headerName.set($event.toUpperCase())"
          (blured)="blured()"
          [autofocus]="true"
        />
      } @else {
        <div
          (click)="onEdit()"
          class="inputTrigger block h-full flex-grow truncate pl-2 text-center leading-[30.5px]"
          #attributeName
          [auxTooltip]="
            attributeName.offsetWidth < attributeName.scrollWidth
              ? uniqTooltip() || headerName()
              : uniqTooltip()
          "
        >
          @if (params.icon) {
            <aux-icon class="mr-2" [name]="params.icon" />
          }

          {{ headerName() }}
        </div>
      }

      <div
        class="flex h-full cursor-pointer items-center border-l px-2"
        (click)="!params.removeColumnDisabled && removeColumn()"
        [auxTooltip]="params.removeColumnTooltip"
      >
        <aux-icon
          class="flex-shrink-0 text-aux-error"
          [class.cursor-not-allowed]="params.removeColumnDisabled"
          [class.opacity-50]="params.removeColumnDisabled"
          name="Trash"
        />
      </div>
    </div>
  `,
})
export class AgInlineHeaderComponent implements IHeaderAngularComp {
  edit = signal(false);
  headerName = signal('');

  params!: AgInlineHeaderComponentParams;

  uniqTooltip = computed(() => {
    const headerName = this.headerName();
    const errorState = this.params.headerErrorState();

    if (headerName && errorState[this.params.field]) {
      const columnNames: string[] = [];

      Object.keys(this.params.getHeaderNames()).forEach((name) => {
        if (this.params.getHeaderNames()[name] === headerName) {
          columnNames.push(this.params.getHeaderNames()[name]);
        }
      });

      return `Attribute name must be unique. ${columnNames.join(', ')} found.`;
    }

    return '';
  });

  @ViewChildren(InputComponent) inp?: InputComponent;

  agInit(params: AgInlineHeaderComponentParams): void {
    this.params = params;
    this.edit.set(!!params.autoFocus);
    this.headerName.set(params.getHeaderNames()[params.column.getColDef().field!]);
  }

  blured(): void {
    this.headerName.set(this.headerName().replace(/\s+/g, ' ').trim());
  }

  onBlur() {
    this.onEdit();
    this.params.onBlur(this.headerName());
  }

  onEdit() {
    if (this.params.editable) {
      this.edit.set(!this.edit());
    }
  }

  refresh() {
    return true;
  }

  removeColumn() {
    this.params.onRemoveColumn().catch(console.error);
  }
}
